import styled, { keyframes, css } from 'styled-components';

/* ─── Brand tokens ─── */
const brand = {
  blue: '#0d4efe',
  teal: '#01ebb2',
  dark: '#1a1a2e',
  textPrimary: '#222',
  textSecondary: '#555',
  textMuted: '#888',
  bg: '#f5f7fa',
  white: '#ffffff',
  danger: '#e74c3c',
  shadow: 'rgba(13, 78, 254, 0.08)',
  shadowHover: 'rgba(13, 78, 254, 0.16)',
};

/* ─── Animations ─── */
const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(13, 78, 254, 0.25); }
  50%  { box-shadow: 0 0 0 8px rgba(13, 78, 254, 0); }
  100% { box-shadow: 0 0 0 0 rgba(13, 78, 254, 0.25); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const stepFadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Page wrapper ─── */
export const SignupPageWrapper = styled.div`
  min-height: 100vh;
  background: ${brand.bg};
  font-family: 'Roboto', sans-serif;
  color: ${brand.textPrimary};
`;

/* ─── Hero / header bar (compact) ─── */
export const HeroBanner = styled.div`
  width: 100%;
  padding: 28px 24px 22px;
  background: linear-gradient(135deg, ${brand.teal} 0%, ${brand.blue} 100%);
  text-align: center;
  color: ${brand.white};
`;

export const HeroTitle = styled.h1`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.55rem;
  font-weight: 800;
  margin: 0 0 4px;
  letter-spacing: -0.3px;

  @media (max-width: 600px) {
    font-size: 1.3rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 0.9rem;
  opacity: 0.92;
  margin: 0 auto;
  font-weight: 400;
  max-width: 480px;
  line-height: 1.4;

  @media (max-width: 600px) {
    font-size: 0.82rem;
  }
`;

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.25);
  margin-top: 16px;
  border-radius: 2px;
  overflow: hidden;
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${brand.white};
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${({ $percent }) => $percent || 0}%;
`;

/* ─── Stepper ─── */
export const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 16px 24px 6px;
  max-width: 760px;
  margin: 0 auto;
`;

export const StepIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

export const StepCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  transition: all 0.35s ease;

  ${({ $active, $completed }) =>
    $completed
      ? css`
          background: ${brand.teal};
          color: ${brand.white};
          box-shadow: 0 4px 14px rgba(1, 235, 178, 0.3);
        `
      : $active
        ? css`
            background: ${brand.blue};
            color: ${brand.white};
            box-shadow: 0 4px 14px rgba(13, 78, 254, 0.3);
            animation: ${pulseRing} 2s ease infinite;
          `
        : css`
            background: #dde3ed;
            color: #999;
          `}
`;

export const StepLabelText = styled.span`
  font-size: 0.72rem;
  margin-top: 6px;
  font-weight: 600;
  color: ${({ $active, $completed }) =>
    $completed ? brand.teal : $active ? brand.blue : '#aaa'};
  font-family: 'Plus Jakarta Sans', sans-serif;
  transition: color 0.3s ease;
  text-align: center;
  max-width: 80px;

  @media (max-width: 480px) {
    font-size: 0.65rem;
    max-width: 56px;
  }
`;

export const StepConnector = styled.div`
  flex: 1;
  height: 3px;
  margin: 0 -4px;
  margin-bottom: 20px;
  border-radius: 2px;
  transition: background 0.4s ease;
  background: ${({ $completed }) =>
    $completed
      ? `linear-gradient(90deg, ${brand.teal}, ${brand.blue})`
      : '#dde3ed'};
`;

/* ─── Card / form container ─── */
export const FormCard = styled.div`
  max-width: ${({ $wide }) => ($wide ? '1200px' : '1100px')};
  margin: 4px auto 40px;
  background: ${brand.white};
  border-radius: 20px;
  box-shadow: 0 8px 32px ${brand.shadow};
  padding: 32px 44px;
  animation: ${fadeSlideUp} 0.45s ease both;
  position: relative;
  overflow: hidden;

  @media (max-width: 600px) {
    margin: 8px 16px 40px;
    padding: 24px 20px;
    border-radius: 16px;
  }
`;

export const StepTitle = styled.h2`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.65rem;
  font-weight: 700;
  color: ${brand.blue};
  margin: 0 0 6px;
`;

export const StepSubtitle = styled.p`
  font-size: 0.92rem;
  color: ${brand.textMuted};
  margin: 0 0 28px;
  line-height: 1.5;
`;

/* ─── Section dividers ─── */
export const SectionDivider = styled.hr`
  border: none;
  border-top: 1.5px solid #eef1f6;
  margin: 28px 0;
`;

export const SectionLabel = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${brand.blue};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 12px;
`;

/* ─── Form fields ─── */
export const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

export const Label = styled.label`
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${brand.textPrimary};
  margin-bottom: 6px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

export const Input = styled.input`
  width: 100%;
  padding: 13px 16px;
  font-size: 0.95rem;
  font-family: 'Roboto', sans-serif;
  border: 2px solid #e0e4ec;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  box-sizing: border-box;
  background: #fafbfd;
  color: ${brand.textPrimary};

  &:focus {
    border-color: ${brand.blue};
    box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.1);
    background: ${brand.white};
  }

  &::placeholder {
    color: #bbb;
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 13px 16px;
  font-size: 0.95rem;
  font-family: 'Roboto', sans-serif;
  border: 2px solid #e0e4ec;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  box-sizing: border-box;
  background: #fafbfd;
  color: ${brand.textPrimary};
  resize: vertical;
  min-height: 80px;
  line-height: 1.5;

  &:focus {
    border-color: ${brand.blue};
    box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.1);
    background: ${brand.white};
  }

  &::placeholder {
    color: #bbb;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 13px 16px;
  font-size: 0.95rem;
  font-family: 'Roboto', sans-serif;
  border: 2px solid #e0e4ec;
  border-radius: 10px;
  outline: none;
  background: #fafbfd;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;

  &:focus {
    border-color: ${brand.blue};
    box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.1);
  }
`;

export const FieldRow = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 520px) {
    flex-direction: column;
    gap: 0;
  }

  & > ${FieldGroup} {
    flex: 1;
  }
`;

export const ErrorText = styled.span`
  font-size: 0.75rem;
  color: ${brand.danger};
  margin-top: 4px;
  display: block;
`;

/* ─── Availability / Urgency chips ─── */
export const AvailabilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

export const AvailabilityChip = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 14px 12px;
  border-radius: 12px;
  border: 2px solid ${({ $selected }) => ($selected ? brand.blue : '#e0e4ec')};
  background: ${({ $selected }) => ($selected ? 'rgba(13, 78, 254, 0.06)' : brand.white)};
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: 'Roboto', sans-serif;
  position: relative;

  &:hover {
    border-color: ${brand.blue};
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(13, 78, 254, 0.12);
  }

  ${({ $selected }) =>
    $selected &&
    css`
      &::after {
        content: '✓';
        position: absolute;
        top: 8px;
        right: 10px;
        font-size: 0.7rem;
        color: ${brand.blue};
        font-weight: 800;
      }
    `}
`;

export const AvailabilityChipIcon = styled.span`
  font-size: 1.5rem;
  line-height: 1;
`;

export const AvailabilityChipLabel = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 0.82rem;
  color: ${({ $selected }) => ($selected ? brand.blue : brand.textPrimary)};
`;

export const AvailabilityChipDesc = styled.span`
  font-size: 0.7rem;
  color: ${brand.textMuted};
  line-height: 1.35;
`;

/* ─── Password field ─── */
export const PasswordWrapper = styled.div`
  position: relative;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  color: inherit;
  opacity: 0.6;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

export const PasswordStrength = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

export const PasswordStrengthBar = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #e0e4ec;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    border-radius: 2px;
    background: ${({ $color }) => $color || '#e0e4ec'};
    width: ${({ $score, $maxScore }) =>
      `${($score / ($maxScore || 1)) * 100}%`};
    transition: width 0.35s ease, background 0.35s ease;
  }
`;

export const PasswordStrengthLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${({ $color }) => $color || brand.textMuted};
  font-family: 'Plus Jakarta Sans', sans-serif;
  min-width: 70px;
`;

export const ConfirmPasswordMatch = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  line-height: 1.35;
  color: ${brand.teal};
`;

/* ─── Review summary ─── */
export const SummaryGrid = styled.div`
  text-align: left;
  background: #f8faff;
  border-radius: 14px;
  padding: 20px 24px;
  margin: 0 0 20px;
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${({ $alignStart }) => ($alignStart ? 'flex-start' : 'center')};
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #eef1f6;
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
  }
`;

export const SummaryLabel = styled.span`
  color: ${brand.textMuted};
  font-weight: 500;
  min-width: 110px;
`;

export const SummaryValue = styled.span`
  color: ${brand.textPrimary};
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-align: right;
`;

/* ─── Review step: read-only timeline checklist ─── */
export const ReviewTimelinesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: auto;
  width: min(320px, 100%);
`;

export const ReviewTimelineRow = styled.div`
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  column-gap: 6px;
  align-items: start;
  width: 100%;
`;

/** Fixed grid column: all checkboxes share the same vertical axis. */
export const ReviewTimelineCheckboxCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  padding-top: 4px;
`;

export const ReviewTimelineText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-top: 6px;
  text-align: right;
`;

export const ReviewTimelineTitle = styled.div`
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: ${brand.textPrimary};
  font-size: 0.88rem;
  line-height: 1.3;
  text-align: right;
`;

export const ReviewTimelineDesc = styled.div`
  font-size: 0.72rem;
  color: ${brand.textMuted};
  line-height: 1.35;
  font-weight: 400;
  font-family: 'Roboto', sans-serif;
  text-align: right;
`;

/* ─── Agreement step: signature block ─── */
export const AgreementSignatureCard = styled.div`
  padding: 22px 24px;
  border-radius: 14px;
  border: 1.5px solid rgba(13, 78, 254, 0.16);
  background: linear-gradient(
    135deg,
    rgba(1, 235, 178, 0.08) 0%,
    rgba(13, 78, 254, 0.06) 100%
  );
  margin-top: 4px;
`;

export const AgreementSignatureHeading = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 1.02rem;
  color: ${brand.blue};
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AgreementSignatureHint = styled.p`
  font-size: 0.78rem;
  color: ${brand.textMuted};
  margin: 10px 0 18px;
  line-height: 1.5;
`;

/* ─── Buttons ─── */
export const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  gap: 12px;
`;

export const PrimaryButton = styled.button`
  padding: 13px 36px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  border-radius: 999px;
  color: ${brand.white};
  background: linear-gradient(135deg, ${brand.teal} 0%, ${brand.blue} 100%);
  background-size: 200% auto;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.3px;

  &:hover:not(:disabled) {
    background-position: right center;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(13, 78, 254, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  padding: 13px 28px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  border: 2px solid #dde3ed;
  border-radius: 999px;
  color: ${brand.textSecondary};
  background: ${brand.white};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${brand.blue};
    color: ${brand.blue};
    transform: translateY(-1px);
  }
`;

/* ─── Confirmation / success ─── */
export const SuccessWrapper = styled.div`
  text-align: center;
  padding: 20px 0;
  animation: ${fadeSlideUp} 0.5s ease both;
`;

export const SuccessIcon = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${brand.teal}, ${brand.blue});
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 2rem;
  color: ${brand.white};
  box-shadow: 0 8px 24px rgba(1, 235, 178, 0.3);
`;

export const SuccessTitle = styled.h2`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 800;
  color: ${brand.blue};
  margin: 0 0 12px;
`;

export const SuccessMessage = styled.p`
  font-size: 1rem;
  color: ${brand.textSecondary};
  line-height: 1.65;
  max-width: 420px;
  margin: 0 auto 28px;
`;

export const TrialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 999px;
  background: rgba(1, 235, 178, 0.08);
  border: 1.5px solid rgba(1, 235, 178, 0.5);
  color: ${brand.blue};
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 0.88rem;
  margin-bottom: 20px;
`;

export const FinePrint = styled.p`
  text-align: center;
  font-size: 0.76rem;
  color: ${brand.textMuted};
  margin-top: 12px;
  line-height: 1.5;
`;

/* ─── Loading overlay ─── */
export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 20px;
  animation: ${fadeIn} 0.25s ease both;
`;

export const LoadingTitle = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
  color: ${brand.textPrimary};
  margin-bottom: 16px;
`;

export const LoadingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
`;

export const LoadingStep = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  font-family: 'Roboto', sans-serif;
  animation: ${stepFadeIn} 0.35s ease both;

  color: ${({ $status }) =>
    $status === 'done'
      ? brand.teal
      : $status === 'active'
        ? brand.blue
        : brand.textMuted};

  font-weight: ${({ $status }) => ($status === 'active' ? 600 : 400)};
`;

export const LoadingStepIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  flex-shrink: 0;

  ${({ $status }) =>
    $status === 'done'
      ? css`
          background: ${brand.teal};
          color: ${brand.white};
        `
      : $status === 'active'
        ? css`
            background: ${brand.blue};
            color: ${brand.white};
          `
        : css`
            background: #e0e4ec;
            color: #999;
          `}
`;

/* ─── Error modal ─── */
export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 26, 46, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
  animation: ${fadeIn} 0.2s ease both;
`;

export const ModalCard = styled.div`
  background: ${brand.white};
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  padding: 36px 32px 28px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  animation: ${fadeSlideUp} 0.35s ease both;

  @media (max-width: 480px) {
    padding: 28px 20px 22px;
    border-radius: 16px;
  }
`;

export const ModalIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(231, 76, 60, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
  font-size: 1.5rem;
`;

export const ModalTitle = styled.h3`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${brand.textPrimary};
  margin: 0 0 10px;
`;

export const ModalBody = styled.p`
  font-size: 0.9rem;
  color: ${brand.textSecondary};
  line-height: 1.6;
  margin: 0 0 24px;
`;

export const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ModalPrimaryButton = styled.button`
  padding: 12px 24px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  border: none;
  border-radius: 999px;
  color: ${brand.white};
  background: linear-gradient(135deg, ${brand.teal} 0%, ${brand.blue} 100%);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(13, 78, 254, 0.25);
  }
`;

export const ModalSecondaryButton = styled.button`
  padding: 10px 24px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: 0.82rem;
  border: none;
  border-radius: 999px;
  color: ${brand.textMuted};
  background: transparent;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${brand.blue};
  }
`;